import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from 'primereact/sidebar';
import { Checkbox } from 'primereact/checkbox';
import classNames from 'classnames';

import { Button, SkeletonLoader, StatusPill } from 'components';
import { toneForStatus } from 'components/StatusPill';
import { routes } from 'constants/index';
import { humanReadable } from 'utils';
import { TicketField } from 'types';

import {
  useListOneTicketQuery,
  useListOneTicketStudiesQuery,
  useListOneApprovalQuery,
} from '../../queries';
import { ListOneTicket } from '../../ListOne.types';
import { ListOneTicketStudy } from '../../ListOneTicketStudies/ListOneTicketStudies.types';

import './list-one-ticket-drawer.scss';

type Tab = 'fields' | 'documents' | 'activity';

const TYPE_NAMES: Record<string, string> = {
  AD: 'Addition',
  DE: 'Deletion',
};

// Fields the validation flagged, whether or not the value moved. Problems get
// a tag; an update is already shown by the highlighted new value.
const FLAGGED_TYPES = ['format', 'mandatory', 'updated'];
const PROBLEM_TYPES = ['format', 'mandatory'];

function isChanged(field: TicketField): boolean {
  return (
    (field.oldValue ?? '') !== (field.newValue ?? '') ||
    FLAGGED_TYPES.includes(field.type)
  );
}

function valueOf(value?: string | null) {
  return value === undefined || value === null || value === '' ? '-' : value;
}

function FieldsTable({ fields }: { fields: TicketField[] }) {
  return (
    <table className="ticket-drawer__fields">
      <thead>
        <tr>
          <th>Field</th>
          <th>Previous value</th>
          <th>New value</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => {
          const changed = isChanged(field);

          return (
            <tr key={field._id || field.field}>
              <td>
                {field.field}
                {PROBLEM_TYPES.includes(field.type) && (
                  <span className="ticket-drawer__flag">{field.type}</span>
                )}
              </td>
              <td>{valueOf(field.oldValue)}</td>
              <td
                className={classNames({
                  'ticket-drawer__changed': changed,
                })}
              >
                {valueOf(field.newValue)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface ListOneTicketDrawerProps {
  ticket: ListOneTicket | null;
  onHide: () => void;
  onReviewDocuments: (ticket: ListOneTicket) => void;
  onFix: (ticket: ListOneTicket) => void;
  canFix: (ticket: ListOneTicket) => boolean;
}

/*
 * A ticket at a glance, beside the list, so people can work down the rows
 * without leaving the page and losing their filters. The full details page is
 * still a click away for anything this leaves out.
 */
export default function ListOneTicketDrawer({
  ticket,
  onHide,
  onReviewDocuments,
  onFix,
  canFix,
}: ListOneTicketDrawerProps) {
  const [tab, setTab] = useState<Tab>('fields');
  const [changedOnly, setChangedOnly] = useState(false);
  const [isUnchangedOpen, setUnchangedOpen] = useState(false);

  // Each ticket opens on all its fields, whatever the last one showed.
  useEffect(() => {
    setTab('fields');
    setChangedOnly(false);
    setUnchangedOpen(false);
  }, [ticket?._id]);

  const ticketId = ticket?.id ?? '';
  const needsApproval = !!ticket?.approval?.required;

  const detailsQuery = useListOneTicketQuery(ticketId, {
    enabled: !!ticketId,
  });
  const studiesQuery = useListOneTicketStudiesQuery(ticketId, {
    enabled: !!ticketId,
  });
  const approvalQuery = useListOneApprovalQuery(ticket?._id ?? '', {
    enabled: !!ticket?._id && needsApproval,
  });

  // The list row already carries the fields, so they show at once and the
  // fetched ticket replaces them when it arrives.
  const fields: TicketField[] =
    detailsQuery.data?.data.data?.fields || ticket?.fields || [];
  const changedFields = fields.filter(isChanged);
  const unchangedFields = fields.filter((field) => !isChanged(field));
  const studies: ListOneTicketStudy[] =
    studiesQuery.data?.data.data?.[0]?.studies || [];
  const approval = approvalQuery.data?.data;
  const documents = approval?.documents || [];

  const country =
    approval?.ticket.country ||
    fields.find(({ field }) => /country/i.test(field))?.newValue;

  const tone = toneForStatus(ticket?.status);
  const isAwaitingDocuments = needsApproval && !ticket?.approval?.approvedAt;
  const showBanner = tone === 'pending' || tone === 'error';

  const tabs: { key: Tab; label: string }[] = [
    { key: 'fields', label: 'Field comparison' },
    {
      key: 'documents',
      label: needsApproval ? `Documents (${documents.length})` : 'Documents',
    },
    {
      key: 'activity',
      label: studiesQuery.isSuccess
        ? `Activity (${studies.length})`
        : 'Activity',
    },
  ];

  function labelForDocument(key: string) {
    return approval?.items.find((item) => item.key === key)?.label || key;
  }

  return (
    <Sidebar
      visible={!!ticket}
      position="right"
      onHide={onHide}
      className="ticket-drawer"
      showCloseIcon
      blockScroll
    >
      {ticket && (
        <div className="ticket-drawer__body">
          <div className="ticket-drawer__head">
            <div>
              <div className="ticket-drawer__eyebrow">Request details</div>
              <h2 className="ticket-drawer__title">
                {TYPE_NAMES[ticket.type] || ticket.type} request
              </h2>
              <div className="ticket-drawer__id">{ticket.id}</div>
            </div>
            <StatusPill status={ticket.status} />
          </div>

          {showBanner && (
            <div
              className={classNames(
                'ticket-drawer__banner',
                `ticket-drawer__banner--${tone}`
              )}
              role="status"
            >
              <i
                className={classNames('pi', {
                  'pi-exclamation-triangle': tone === 'pending',
                  'pi-times-circle': tone === 'error',
                })}
                aria-hidden="true"
              />
              <div>
                <div className="ticket-drawer__banner-title">
                  {isAwaitingDocuments
                    ? 'Documents required'
                    : tone === 'error'
                    ? 'Needs fixing'
                    : 'Waiting'}
                </div>
                <div>{ticket.statusDescription || 'No description given.'}</div>
              </div>
            </div>
          )}

          <dl className="ticket-drawer__summary">
            <div>
              <dt>Employee ID</dt>
              <dd>{valueOf(ticket.employeeId)}</dd>
            </div>
            <div>
              <dt>Person ID</dt>
              <dd>{valueOf(ticket.PERSONID)}</dd>
            </div>
            <div>
              <dt>Country</dt>
              <dd>{valueOf(country)}</dd>
            </div>
            <div>
              <dt>Created</dt>
              {/* The day is enough here; the time is in the table. */}
              <dd>{valueOf(humanReadable(ticket.createdDate).split(',')[0])}</dd>
            </div>
          </dl>

          <div className="ticket-drawer__tabs" role="tablist">
            {tabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={classNames('ticket-drawer__tab', {
                  'ticket-drawer__tab--active': tab === key,
                })}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="ticket-drawer__panel" role="tabpanel">
            {tab === 'fields' &&
              (detailsQuery.isLoading && fields.length === 0 ? (
                <SkeletonLoader height="12rem" />
              ) : fields.length === 0 ? (
                <p className="ticket-drawer__empty">
                  This ticket has no fields to compare.
                </p>
              ) : (
                <>
                  {/* How many fields changed, beside the switch that narrows the
                      table to them. */}
                  <div className="ticket-drawer__fields-bar">
                    <span className="ticket-drawer__fields-count">
                      {fields.length} fields · {changedFields.length} changed
                    </span>
                    <label className="ticket-drawer__toggle">
                      <Checkbox
                        checked={changedOnly}
                        onChange={(event) => setChangedOnly(!!event.checked)}
                      />
                      <span>Changed fields only</span>
                    </label>
                  </div>

                  {changedOnly ? (
                    <>
                      {changedFields.length > 0 ? (
                        <FieldsTable fields={changedFields} />
                      ) : (
                        <p className="ticket-drawer__empty">
                          No field differs from its previous value.
                        </p>
                      )}

                      {unchangedFields.length > 0 && (
                        <div className="ticket-drawer__section">
                          <button
                            type="button"
                            className="ticket-drawer__section-toggle"
                            aria-expanded={isUnchangedOpen}
                            onClick={() => setUnchangedOpen((open) => !open)}
                          >
                            <i
                              className={classNames('pi', {
                                'pi-chevron-down': isUnchangedOpen,
                                'pi-chevron-right': !isUnchangedOpen,
                              })}
                              aria-hidden="true"
                            />
                            Unchanged fields ({unchangedFields.length})
                          </button>
                          {isUnchangedOpen && (
                            <FieldsTable fields={unchangedFields} />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <FieldsTable fields={fields} />
                  )}
                </>
              ))}

            {tab === 'documents' &&
              (!needsApproval ? (
                <p className="ticket-drawer__empty">
                  No documents are needed for tickets from this country.
                </p>
              ) : approvalQuery.isLoading ? (
                <SkeletonLoader height="8rem" />
              ) : (
                <>
                  {ticket.approval?.approvedAt && (
                    <p className="ticket-drawer__note">
                      Approved
                      {ticket.approval.approvedByName
                        ? ` by ${ticket.approval.approvedByName}`
                        : ''}{' '}
                      on {humanReadable(ticket.approval.approvedAt)}.
                    </p>
                  )}

                  {documents.length === 0 ? (
                    <p className="ticket-drawer__empty">
                      Nothing has been uploaded yet.
                    </p>
                  ) : (
                    <ul className="ticket-drawer__list">
                      {documents.map((document) => (
                        <li key={`${document.key}-${document.uploadedAt}`}>
                          <div className="ticket-drawer__list-main">
                            <span>{labelForDocument(document.key)}</span>
                            <a
                              href={document.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {document.filename || document.key}
                            </a>
                          </div>
                          <div className="ticket-drawer__list-meta">
                            {document.uploadedByName}
                            {document.uploadedAt &&
                              `, ${humanReadable(document.uploadedAt)}`}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {!!approval?.missing.length && (
                    <p className="ticket-drawer__note ticket-drawer__note--warn">
                      Still missing:{' '}
                      {approval.missing.map(labelForDocument).join(', ')}.
                    </p>
                  )}
                </>
              ))}

            {tab === 'activity' &&
              (studiesQuery.isLoading ? (
                <SkeletonLoader height="8rem" />
              ) : studies.length === 0 ? (
                <p className="ticket-drawer__empty">No activity yet.</p>
              ) : (
                <ul className="ticket-drawer__list">
                  {studies.map((study) => (
                    <li key={study._id}>
                      <div className="ticket-drawer__list-main">
                        <span>{study.statusDescription || study.status}</span>
                        <StatusPill status={study.status} />
                      </div>
                      <div className="ticket-drawer__list-meta">
                        {humanReadable(study.createdDate)}
                      </div>
                    </li>
                  ))}
                </ul>
              ))}
          </div>

          <div className="ticket-drawer__footer">
            <Link
              to={`${routes.listOne.index}/${ticket.id}`}
              className="ticket-drawer__secondary"
            >
              Open full details
            </Link>

            {isAwaitingDocuments ? (
              <Button
                label="Review documents"
                onClick={() => onReviewDocuments(ticket)}
              />
            ) : (
              canFix(ticket) && (
                <Button label="Fix" onClick={() => onFix(ticket)} />
              )
            )}
          </div>
        </div>
      )}
    </Sidebar>
  );
}
